import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtEur } from "@/components/ui-kit";
import { LetturaAI, giudizioScarto } from "@/components/commessa/ArtigianiAI";
import { TIPI_SOGGETTO } from "@/lib/margineCommessa";
import { useNomiSoggetti } from "@/components/commessa/AffidaDialog";

const abbinaAuto = (righe, items) => {
  const usati = new Set();
  return righe.map((r) => {
    const best = items.find((it) => r.voce_id && it.voce_id === r.voce_id && !usati.has(it.id) && !it.affidamento);
    if (best) usati.add(best.id);
    return { ...r, item_id: best?.id || "" };
  });
};

// Preventivo ricevuto da artigiano/fornitore/professionista: l'AI legge le righe, ogni riga "copre" una voce del computo.
export function PreventivoRicevutoDialog({ open, onOpenChange, cid, items, preselezione, onDone }) {
  const nomi = useNomiSoggetti();
  const [righe, setRighe] = useState([]);
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("artigiano");
  const [busy, setBusy] = useState(false);
  const [sovrascrivi, setSovrascrivi] = useState(false);
  useEffect(() => { if (open) { setRighe([]); setNome(""); setSovrascrivi(false); } }, [open]);
  const pool = preselezione?.length ? items.filter((it) => preselezione.includes(it.id)) : items;
  const onLetto = (r) => {
    setNome(r.azienda || "");
    setRighe(abbinaAuto((r.righe || []).map((x) => ({ ...x, totale: x.totale != null ? Number(x.totale) : (Number(x.prezzo_unit) || 0) * (Number(x.qty) || 1) })), pool.length ? pool : items));
  };
  const upd = (i, k, v) => setRighe(righe.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const byItem = {};
  righe.forEach((r) => { if (r.item_id) byItem[r.item_id] = (byItem[r.item_id] || 0) + (Number(r.totale) || 0); });
  const coperte = items.filter((it) => byItem[it.id] != null);
  const giaAffidate = coperte.filter((it) => it.affidamento);
  const totLoro = righe.reduce((s, r) => s + (Number(r.totale) || 0), 0);
  const totPrev = coperte.reduce((s, it) => s + (Number(it.costo_previsto) || 0), 0);
  const extra = righe.filter((r) => !r.item_id).reduce((s, r) => s + (Number(r.totale) || 0), 0);
  const pctTot = totPrev ? ((totLoro - extra - totPrev) / totPrev) * 100 : null;
  const save = async () => {
    if (!nome.trim()) return toast.error("Indica il nome di chi ha fatto il preventivo");
    if (!righe.length) return toast.error("Carica prima il preventivo");
    setBusy(true);
    try {
      await api.post(`/commesse/${cid}/workflow/artigiani-preventivi`, {
        artigiano_nome: nome.trim(), tipo_soggetto: tipo, importo_offerto: totLoro, voci_riferite: [],
        righe_confronto: righe.map((r) => ({ descrizione: r.descrizione, qty: r.qty, unit: r.unit, totale: Number(r.totale) || 0, item_id: r.item_id || null })),
        confronto_totali: { totale_loro: totLoro, totale_nostro: totPrev, scarto_pct: pctTot },
        affida_righe: Object.entries(byItem).map(([item_id, costo]) => ({ item_id, costo })),
        extra_non_previsto: extra, sovrascrivi,
      });
      toast.success(`Preventivo salvato: ${coperte.length} voci coperte`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto" data-testid="prev-ricevuto-dialog">
        <DialogHeader>
          <DialogTitle>Carica preventivo ricevuto</DialogTitle>
          <DialogDescription>Ogni riga del loro preventivo copre una voce del computo: le voci coperte spariscono da "da affidare" e il loro prezzo diventa il costo reale.</DialogDescription>
        </DialogHeader>
        <LetturaAI onLetto={onLetto} />
        {righe.length > 0 && (<>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Chi l'ha fatto</Label><Input value={nome} onChange={(e) => setNome(e.target.value)} list="prev-ric-nomi" data-testid="prev-ric-nome" /><datalist id="prev-ric-nomi">{nomi.map((n) => <option key={n} value={n} />)}</datalist></div>
            <div><Label>Tipo</Label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="w-full h-9 border border-zinc-300 rounded px-2 text-sm" data-testid="prev-ric-tipo">
                {TIPI_SOGGETTO.filter((t) => t.value !== "interno").map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <table className="w-full text-xs border rounded" data-testid="prev-ric-righe">
            <thead className="bg-zinc-50 text-[10px] uppercase text-zinc-500"><tr><th className="text-left px-2 py-1.5">Riga del loro preventivo</th><th className="text-right px-2">Loro prezzo</th><th className="text-left px-2 w-72">Copre la nostra voce</th><th className="text-right px-2">Nostro previsto</th><th className="px-2">Esito</th></tr></thead>
            <tbody>
              {righe.map((r, i) => {
                const it = items.find((x) => x.id === r.item_id);
                const g = it ? giudizioScarto(it.costo_previsto ? ((byItem[it.id] - it.costo_previsto) / it.costo_previsto) * 100 : null) : null;
                return (
                  <tr key={i} className="border-t" data-testid={`prev-ric-riga-${i}`}>
                    <td className="px-2 py-1.5">{r.descrizione}<div className="text-[10px] text-zinc-500">{r.qty} {r.unit}</div></td>
                    <td className="px-2 py-1"><Input type="number" value={r.totale} onChange={(e) => upd(i, "totale", e.target.value)} className="h-7 text-xs text-right w-24 ml-auto" data-testid={`prev-ric-tot-${i}`} /></td>
                    <td className="px-2 py-1">
                      <select value={r.item_id} onChange={(e) => upd(i, "item_id", e.target.value)} className="w-full h-7 border border-zinc-300 rounded px-1 text-xs" data-testid={`prev-ric-item-${i}`}>
                        <option value="">— non previsto da noi (costo extra) —</option>
                        {items.map((x) => <option key={x.id} value={x.id}>{x.affidamento ? "✓ " : ""}{x.name} · {x.qty} {x.unit}</option>)}
                      </select>
                    </td>
                    <td className="px-2 text-right mono">{it ? fmtEur(it.costo_previsto) : "—"}</td>
                    <td className="px-2 text-center">{g ? <span className={`px-1.5 py-0.5 rounded text-[10px] ${g.cls}`}>{g.txt}</span> : <span className="text-[10px] text-amber-700">extra</span>}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-zinc-50 font-semibold"><tr>
              <td className="px-2 py-2">Totale loro preventivo</td><td className="px-2 text-right mono" data-testid="prev-ric-tot-loro">{fmtEur(totLoro)}</td>
              <td className="px-2 text-[11px] font-normal">{coperte.length} voci coperte{extra ? ` · ${fmtEur(extra)} non previsti` : ""}</td>
              <td className="px-2 text-right mono" data-testid="prev-ric-tot-prev">{fmtEur(totPrev)}</td>
              <td className="px-2 text-center">{pctTot != null && <span className={`px-1.5 py-0.5 rounded text-[10px] ${giudizioScarto(pctTot).cls}`}>{pctTot > 0 ? "+" : ""}{pctTot.toFixed(0)}%</span>}</td>
            </tr></tfoot>
          </table>
          {giaAffidate.length > 0 && (
            <label className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-900" data-testid="prev-ric-gia-affidate">
              <input type="checkbox" checked={sovrascrivi} onChange={(e) => setSovrascrivi(e.target.checked)} className="mt-0.5" data-testid="prev-ric-sovrascrivi" />
              <span>{giaAffidate.length} voci sono già affidate ({giaAffidate.map((x) => `${x.name} → ${x.affidamento.nome}`).join(", ")}). Spunta per sostituire l'affidamento con questo preventivo; altrimenti restano affidate come sono e il prezzo di quelle righe viene conteggiato come costo extra. Se poi elimini questo preventivo, torna l'affidamento precedente.</span>
            </label>
          )}
        </>)}
        <DialogFooter><Button onClick={save} disabled={busy || !righe.length} style={{ background: "var(--brand)", color: "white" }} data-testid="prev-ric-save">Salva e copri le voci</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
